import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  RiTrophyLine,
  RiAwardLine,
  RiMedalLine,
  RiCashLine,
  RiBookOpenLine,
  RiUserStarLine,
  RiTeamLine,
  RiGlobalLine,
  RiArrowRightLine,
  RiCheckLine,
  RiCalendarEventLine,
  RiMailLine,
  RiPhoneLine,
  RiPresentationLine,
  RiMicroscopeLine,
  RiAwardLine as RiAwardIcon,
  RiPriceTag3Line,
  RiTimeLine,
} from 'react-icons/ri';
import { useSummit } from '../pages/summitdetails';

/* ─── THEME COLORS (Teal & Gold Palette) ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradPurple: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
  gradGreen: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
};

/* ─── CSS (with responsive overrides) ─────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  /* HERO */
  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(40px,7vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  /* CARDS */
  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:clamp(20px,3vw,28px);box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}
  .t-card-header{display:flex;align-items:center;gap:12px;margin-bottom:20px}
  .t-card-icon{width:48px;height:48px;border-radius:50%;background:${T.softTeal};display:flex;align-items:center;justify-content:center}
  .t-card-title{font-size:20px;font-weight:800;color:${T.text}}

  /* BENEFIT ROW */
  .t-benefit-row{display:flex;align-items:flex-start;gap:14px;padding:13px 15px;border-radius:13px;border:1px solid transparent;margin-bottom:10px;transition:all 0.25s ease}
  .t-benefit-row:hover{background:${T.softTeal};border-color:${T.primaryLight};transform:translateX(6px)}

  /* STEP CARD */
  .t-step-card{background:${T.offWhite};border:1px solid ${T.borderLight};border-radius:18px;padding:24px 20px;transition:all 0.3s ease}
  .t-step-card:hover{border-color:${T.primary};background:${T.softTeal};transform:translateY(-4px)}

  /* CRITERIA BAR */
  .t-criteria-bar{background:${T.borderLight};border-radius:99px;height:10px;overflow:hidden}
  .t-criteria-fill{height:100%;border-radius:99px;transition:width 1.2s cubic-bezier(0.4,0,0.2,1)}

  /* CTA */
  .t-cta-w{padding:0 clamp(16px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(24px,4vw,52px) clamp(24px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-r{display:flex;flex-direction:row;align-items:center;gap:16px;flex-wrap:wrap}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  /* FOOTER */
  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(16px,4vw,64px);margin-top:40px}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}

  @keyframes slideIn{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
  @keyframes floatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}

  /* ─── RESPONSIVE OVERRIDES ─── */
  @media (max-width: 1024px) {
    .t-hero-i { flex-direction: column; align-items: center; text-align: center; }
    .t-hero-p { max-width: 100%; margin: 0 auto; }
    .t-stats { align-self: center; }
    .awards-grid-3 { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }
    .awards-grid-4 { grid-template-columns: repeat(2, 1fr) !important; gap: 20px !important; }
    .awards-steps { grid-template-columns: repeat(2, 1fr) !important; gap: 16px !important; }
    .awards-benefits { grid-template-columns: 1fr !important; gap: 20px !important; }
  }
  @media (max-width: 768px) {
    .t-hero { padding: 32px 16px 24px; }
    .t-hero-h { font-size: 2rem; }
    .t-hero-p { font-size: 0.9rem; }
    .t-stats { flex-direction: column; width: 100%; border-radius: 12px; }
    .t-st { padding: 16px; align-items: center; min-width: auto; width: 100%; }
    .t-st-n { font-size: 1.5rem; }
    .t-cta { flex-direction: column; text-align: center; padding: 28px 20px; }
    .t-cta-r { width: 100%; flex-direction: column; align-items: center; }
    .t-cta-btn, .t-cta-btn-outline { width: 100%; justify-content: center; }
    .t-footer { padding: 20px 16px; }
    .t-fi { flex-direction: column; text-align: center; gap: 8px; }
    .awards-grid-3 { grid-template-columns: 1fr !important; gap: 16px !important; }
    .awards-grid-4 { grid-template-columns: 1fr !important; gap: 16px !important; }
    .awards-steps { grid-template-columns: 1fr !important; gap: 14px !important; }
    .awards-pills { gap: 10px; }
    .awards-pills > div { min-width: auto !important; width: 100%; justify-content: center; }
    .awards-pills .emoji-wrap { width: 36px !important; height: 36px !important; font-size: 16px !important; }
    .awards-pills .label { font-size: 12px !important; }
    .awards-pills .sub { font-size: 10px !important; }
    .awards-criteria { padding: 24px 20px !important; }
    .awards-criteria h3 { font-size: 18px !important; }
    .awards-criteria .bar-label { font-size: 13px !important; }
    .awards-section-title { font-size: 24px !important; padding-left: 0 !important; text-align: center; }
    .awards-section-sub { padding-left: 0 !important; text-align: center; }
  }
  @media (max-width: 480px) {
    .t-hero { padding: 24px 12px 20px; }
    .t-hero-h { font-size: 1.6rem; }
    .t-hero-lbl { font-size: 10px; padding: 4px 12px; }
    .t-st-n { font-size: 1.3rem; }
    .t-card { padding: 16px; }
    .t-cta-h { font-size: 1.2rem; }
    .t-cta-p { font-size: 0.9rem; }
    .awards-pills > div { padding: 10px 14px !important; }
    .awards-pills .emoji-wrap { width: 32px !important; height: 32px !important; font-size: 14px !important; }
    .awards-steps .step-card { padding: 16px !important; }
    .awards-steps .step-card h4 { font-size: 14px !important; }
    .awards-steps .step-card p { font-size: 12px !important; }
    .awards-criteria { padding: 16px !important; }
    .awards-criteria h3 { font-size: 16px !important; }
    .awards-criteria .bar-label { font-size: 12px !important; }
    .awards-section-title { font-size: 20px !important; }
    .awards-section-sub { font-size: 13px !important; }
    .t-cta-btn, .t-cta-btn-outline { font-size: 13px; padding: 12px 20px; }
  }
`;

const Awards = () => {
  // ✅ Get eventId from context
  let eventId = null;
  try {
    const summitContext = useSummit();
    eventId = summitContext?.eventId || null;
  } catch (error) {
    console.log('Awards component rendered outside Summit context');
    eventId = null;
  }

  const [visible, setVisible] = useState({});
  const refs = useRef({});
  const [barsVisible, setBarsVisible] = useState(false);
  const barsRef = useRef(null);

  useEffect(() => {
    const observers = {};
    Object.keys(refs.current).forEach(key => {
      if (!refs.current[key]) return;
      observers[key] = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setVisible(prev => ({ ...prev, [key]: true })); },
        { threshold: 0.1 }
      );
      observers[key].observe(refs.current[key]);
    });
    return () => Object.values(observers).forEach(o => o.disconnect());
  }, []);

  useEffect(() => {
    if (!barsRef.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setBarsVisible(true); }, { threshold: 0.15 });
    obs.observe(barsRef.current);
    return () => obs.disconnect();
  }, []);

  const fadeUp = (key, delay = 0) => ({
    ref: el => (refs.current[key] = el),
    style: {
      opacity: visible[key] ? 1 : 0,
      transform: visible[key] ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
    },
  });

  const posterCriteria = [
    { label: 'Originality of Topic', pct: 25, color: T.primary },
    { label: 'Content (Introduction, Methods, Results, Conclusions)', pct: 25, color: T.accent },
    { label: 'Educational Value', pct: 20, color: '#8B5CF6' },
    { label: 'Layout & Design (Clear and appropriate)', pct: 15, color: '#10B981' },
    { label: 'Overall Impression', pct: 15, color: T.accentGold },
  ];

  return (
    <div style={{ background: T.offWhite, minHeight: '100vh' }}>
      <style>{css}</style>

      {/* ══════════ HERO ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiAwardIcon size={13} />
              Meeting21 · Recognition Program
            </div>
            <h1 className="t-hero-h">
              Awards &amp; <em>Recognition</em>
            </h1>
            <p className="t-hero-p">
              Meeting21 celebrates scientific excellence through prestigious awards in academic research.
              Submit your best work and earn recognition among the world's leading researchers.
            </p>
            
            {/* Decorative line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24, justifyContent: 'center' }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">5+</span>
              <span className="t-st-l">Awards</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">100%</span>
              <span className="t-st-l">Recognition</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">2026</span>
              <span className="t-st-l">Edition</span>
            </div>
          </div>
        </div>
      </section>

      {/* Award Type Pills - Teal & Gold themed */}
      <div className="awards-pills" style={{ maxWidth: 1320, margin: '0 auto', padding: '24px clamp(16px,4vw,64px) 0', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
        {[
          { emoji: '🖼️', label: 'Best Poster Award', sub: '3 winners', col: T.primary },
          { emoji: '🔬', label: 'Young Scientist Award', sub: '2 winners · under 35', col: T.accentGold },
          { emoji: '📚', label: 'Journal Publication', sub: 'Scopus-indexed', col: '#8B5CF6' },
          { emoji: '🧭', label: 'Career Mentoring', sub: 'Guidance & networking', col: '#10B981' },
        ].map((p, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: T.white, border: `1px solid ${p.col}30`,
            borderRadius: 14, padding: '12px 20px', minWidth: 220,
            boxShadow: T.shadow,
            animation: `slideIn 0.5s ease ${0.25 + i * 0.1}s both`,
          }}>
            <div className="emoji-wrap" style={{ width: 40, height: 40, borderRadius: 10, background: `${p.col}15`, border: `1px solid ${p.col}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0 }}>{p.emoji}</div>
            <div style={{ textAlign: 'left' }}>
              <div className="label" style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{p.label}</div>
              <div className="sub" style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>{p.sub}</div>
            </div>
            <div style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: p.col, flexShrink: 0 }} />
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px clamp(16px,4vw,64px) 80px' }}>

        {/* ── BEST POSTER AWARD ── */}
        <section style={{ marginBottom: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ width: 54, height: 54, borderRadius: 15, background: T.gradPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0, boxShadow: T.shadow }}>🖼️</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.primary, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>Award Category 01</div>
              <h2 className="awards-section-title" style={{ fontSize: 28, fontWeight: 800, color: T.text, margin: 0 }}>Best Poster Award</h2>
            </div>
          </div>
          <p className="awards-section-sub" style={{ fontSize: 15, color: T.textMid, lineHeight: 1.7, marginBottom: 32, paddingLeft: 70, textAlign: 'center' }}>
            Poster presentation offers a unique opportunity to combine visual and oral explanations of your projects.
            Three Best Posters will be awarded at the closing ceremony of the conference.
          </p>

          {/* Benefit Cards */}
          <div className="awards-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, marginBottom: 40 }}>
            {[
              { icon: '🏅', title: '3 Awards Given', desc: 'Three best posters recognized at the closing ceremony of the conference.', col: T.primary },
              { icon: '📰', title: 'Journal Publication', desc: 'Best posters get an opportunity to publish their full work in supporting journals at discounted rates.', col: T.accentGold },
              { icon: '💵', title: 'Cash Prizes & Certificate', desc: 'Awardees will receive exciting cash prizes and a certificate of appreciation.', col: '#8B5CF6' },
            ].map((c, i) => (
              <div key={i} className="t-card" style={{ padding: '28px' }}>
                <div style={{ fontSize: 38, marginBottom: 16 }}>{c.icon}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 10 }}>{c.title}</h3>
                <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.65 }}>{c.desc}</p>
                <div style={{ width: 40, height: 4, background: c.col, borderRadius: 99, marginTop: 16 }} />
              </div>
            ))}
          </div>

          {/* Selection Criteria */}
          <div ref={barsRef} className="t-card awards-criteria" style={{ padding: '36px 40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 6 }}>📊 Selection Criteria</h3>
                <p style={{ fontSize: 13, color: T.textMuted }}>Posters are evaluated across five dimensions by the scientific committee.</p>
              </div>
              <span style={{ background: T.softTeal, border: `1px solid ${T.borderLight}`, borderRadius: 99, padding: '5px 14px', fontSize: 12, color: T.primary, fontWeight: 700 }}>100% Total</span>
            </div>
            {posterCriteria.map((c, i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span className="bar-label" style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{c.label}</span>
                  <span style={{ fontSize: 14, color: c.color, fontWeight: 700 }}>{c.pct}%</span>
                </div>
                <div className="t-criteria-bar">
                  <div className="t-criteria-fill" style={{
                    width: barsVisible ? `${c.pct * 4}%` : '0%',
                    background: c.color,
                    transitionDelay: `${i * 0.15}s`
                  }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── YOUNG SCIENTIST AWARD ── */}
        <section style={{ marginBottom: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ width: 54, height: 54, borderRadius: 15, background: T.gradGold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0, boxShadow: T.shadowGold }}>🔬</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.accentGold, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>Award Category 02</div>
              <h2 className="awards-section-title" style={{ fontSize: 28, fontWeight: 800, color: T.text, margin: 0 }}>Young Scientist Award</h2>
            </div>
          </div>
          <p className="awards-section-sub" style={{ fontSize: 15, color: T.textMid, lineHeight: 1.7, marginBottom: 32, paddingLeft: 70, textAlign: 'center' }}>
            Meeting21 proudly presents the Young Scientist Awards to encourage and recognize excellence in research among young researchers.
            Two researchers will be selected.
          </p>

          {/* Eligibility + Benefits */}
          <div className="awards-benefits" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 24, marginBottom: 32 }}>
            {/* Eligibility */}
            <div className="t-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>✅</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.primary, margin: 0 }}>Eligibility Criteria</h3>
              </div>
              {[
                { icon: '🎂', text: 'Researchers and scientists below 35 years of age' },
                { icon: '📝', text: 'Must submit an abstract as the first author' },
                { icon: '🎤', text: 'Presentation must be in Oral format' },
                { icon: '🪪', text: 'Must provide a copy of their passport (first page) as proof of age' },
              ].map((item, i) => (
                <div key={i} className="t-benefit-row">
                  <span style={{ fontSize: 19, flexShrink: 0 }}>{item.icon}</span>
                  <p style={{ fontSize: 14, color: T.textMid, margin: 0, lineHeight: 1.65 }}>{item.text}</p>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="t-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: `${T.accentGold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🎁</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: T.accentGold, margin: 0 }}>Benefits for Awardees</h3>
              </div>
              {[
                { icon: '👥', text: 'Two researchers will be selected at the conference', highlight: true },
                { icon: '💰', text: 'Exciting cash prizes + Certificate of Appreciation', highlight: false },
                { icon: '🧭', text: 'Career guidance and mentoring opportunities', highlight: false },
                { icon: '📚', text: 'Opportunity to publish in Scopus-indexed journals at discounted rates', highlight: false },
              ].map((item, i) => (
                <div key={i} className="t-benefit-row" style={item.highlight ? { background: `${T.accentGold}10`, borderColor: T.accentGold } : {}}>
                  <span style={{ fontSize: 19, flexShrink: 0 }}>{item.icon}</span>
                  <p style={{ fontSize: 14, color: item.highlight ? T.text : T.textMuted, margin: 0, lineHeight: 1.65, fontWeight: item.highlight ? 600 : 400 }}>{item.text}</p>
                </div>
              ))}
              <div style={{ marginTop: 16, background: `${T.accentGold}10`, border: `1px solid ${T.accentGold}30`, borderRadius: 13, padding: '13px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                <p style={{ fontSize: 13, color: T.accentGoldDark, margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                  Applicants must provide a copy of their passport (first page) as proof of age.
                </p>
              </div>
            </div>
          </div>

          {/* How to Participate */}
          <div className="t-card">
            <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>🗺️ How to Participate</h3>
            <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 28 }}>Four steps to win a prestigious award at Meeting21.</p>
            <div className="awards-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20 }}>
              {[
                { step: '01', label: 'Submit Abstract', desc: 'Register and submit your research abstract as first author', color: T.primary, icon: '📋' },
                { step: '02', label: 'Get Accepted', desc: 'Receive confirmation and prepare your oral presentation', color: T.accent, icon: '✅' },
                { step: '03', label: 'Present', desc: 'Deliver your research at Meeting21 in oral format', color: '#8B5CF6', icon: '🎤' },
                { step: '04', label: 'Win Award', desc: 'Top candidates recognized at the closing ceremony', color: T.accentGold, icon: '🏆' },
              ].map((s, i) => (
                <div key={i} className="t-step-card step-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 13, background: `${s.color}15`, border: `1px solid ${s.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: s.color }}>{s.step}</div>
                    <span style={{ fontSize: 26 }}>{s.icon}</span>
                  </div>
                  <h4 style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 8 }}>{s.label}</h4>
                  <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.55 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY PARTICIPATE ── */}
        <section style={{ marginBottom: 64 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="t-hero-lbl" style={{ display: 'inline-flex' }}>Why Participate</div>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: T.text, marginTop: 8 }}>What You Gain</h2>
            <div style={{ width: 80, height: 3, background: T.gradPrimary, borderRadius: 2, margin: '16px auto 0' }} />
          </div>
          <div className="awards-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24 }}>
            {[
              { icon: '🌐', title: 'Global Visibility', desc: 'Gain international recognition in the research community worldwide.', col: T.primary },
              { icon: '🤝', title: 'Networking', desc: 'Connect with leading scientists and researchers from around the world.', col: T.accentGold },
              { icon: '📖', title: 'Publication', desc: 'Fast-track publication in Scopus-indexed journals at discounted rates.', col: '#8B5CF6' },
              { icon: '🚀', title: 'Career Boost', desc: 'Mentoring and career guidance to accelerate your research journey.', col: '#10B981' },
            ].map((w, i) => (
              <div key={i} className="t-card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 34, marginBottom: 16 }}>{w.icon}</div>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>{w.title}</h4>
                <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.65 }}>{w.desc}</p>
                <div style={{ width: 40, height: 4, background: w.col, borderRadius: 99, margin: '16px auto 0' }} />
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA Section ── */}
        <div className="t-cta-w" style={{ padding: 0 }}>
          <motion.div
            className="t-cta"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <div>
              <div className="t-cta-tag">Submissions Now Open</div>
              <h2 className="t-cta-h">Ready to Claim Your Award?</h2>
              <p className="t-cta-p">Submit your abstract now and stand a chance to win prestigious awards at Meeting21 — celebrating the best in global research.</p>
            </div>
            <div className="t-cta-r">
              <Link to="/abstract-submission" className="t-cta-btn">
                Submit Abstract <RiArrowRightLine size={15} />
              </Link>
              <Link 
                to={`/contact${eventId ? `?eventId=${eventId}` : ''}`}  
                className="t-cta-btn-outline"
              >
                Contact Us <RiArrowRightLine size={15} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

  
    </div>
  );
};

export default Awards;